import React, { useCallback, useEffect, useRef } from 'react';
import PolicyHolderReviewClaimModal from '../../components/policyHolderReviewClaim/policyHolderReviewClaim';
import * as action from '../../actions';

import { useParams } from 'react-router-dom';
import claimReviewProjections from '../../projections/claimReview';
import PageNotFound from '../../components/pageNotFound/pageNotFound';
// @ts-ignore
import { Loader } from 'arag-ui-lib';
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import { syncTranslationsFromRedux } from '../../i18n/syncTranslationsFromRedux';
import { store } from '../../store/store';
import codes from '../../utils/constants/codes';
import applicationSettingsProjections from '../../projections/applicationSettings';
type AcceptRejectedObject = {
    title: string;
    subtitle: string;
    contact: string;
    aragTelephoneNumber: string | number;
    languageId: string;
};

const PolicyHolderReviewClaimContainer = (): JSX.Element => {
    const dispatch = useAppDispatch();
    const isClaimReviewValid = useAppSelector(
        state => state.claimReview.isClaimReviewValid,
    );
    const claimReview = useAppSelector(state => state.claimReview.claimReview);
    const languageId = useAppSelector(
        state => state.applicationUser.unAuthorisedDetails.languageId,
    );
    const claimReviewAnswersGroupedByStep = useAppSelector(
        claimReviewProjections.claimReviewAnswersGroupedByStep(),
    ) as Array<any>;
    const claimReviewOverview = useAppSelector(
        claimReviewProjections.claimReviewOverview,
    );
    const acceptedRejectedWordings: AcceptRejectedObject[] = useAppSelector(
        claimReviewProjections.acceptedRejectedWordings,
    );
    const rejectedReasonClaimReviewWordings = useAppSelector(
        state => state.claimReview.rejectedReasonClaimReview,
    );
    const wordings = useAppSelector(state => state.applicationUser.wordings);
    const languages = useAppSelector(
        state => state.applicationSettings.apiApplicationSettings.languages,
    );
    const submitting = useAppSelector(state => state.claimReview.submitting);
    const loading = useAppSelector(state => state.claimReview.loading);
    const rejectFormLoading = useAppSelector(
        state => state.claimReview.rejectFormLoading,
    );
    let {
        claimApplicationId,
        randomId,
    }: { claimApplicationId: string; randomId: string } = useParams();
    const currentCountryCode: string | null = useAppSelector(
        applicationSettingsProjections.getCountry,
    );
    const hasInitialised = useRef(false);

    const fetchClaimReviewValidityAction = useCallback(
        (payload: action.fetchClaimReviewValidityAction) =>
            dispatch(action.fetchClaimReviewValidity(payload)),
        [dispatch],
    );
    const fetchClaimReviewDetailsAction = useCallback(
        (payload: action.fetchClaimReviewDetailsAction) =>
            dispatch(action.fetchClaimReviewDetails(payload)),
        [dispatch],
    );

    const acceptClaimReviewAction = useCallback(
        (payload: action.acceptClaimReviewAction) =>
            dispatch(action.acceptClaimReview(payload)),
        [dispatch],
    );

    const fetchRejectionFormClaimReviewAction = useCallback(
        (payload: action.fetchRejectionFormClaimReviewAction) =>
            dispatch(action.fetchRejectionFormClaimReview(payload)),
        [dispatch],
    );

    const rejectClaimReviewWithReasonAction = useCallback(
        (payload: action.rejectClaimReviewWithReasonAction) =>
            dispatch(action.rejectClaimReviewWithReason(payload)),
        [dispatch],
    );

    const changeClaimReviewAnswerStateAction = useCallback(
        (payload: action.changeClaimReviewAnswerStateAction) =>
            dispatch(action.changeClaimReviewAnswerState(payload)),
        [dispatch],
    );

    const changeClaimReviewStateAction = useCallback(
        (payload: action.changeClaimReviewStateAction) =>
            dispatch(action.changeClaimReviewState(payload)),
        [dispatch],
    );

    const fetchUnauthorisedUserWordingsAction = useCallback(
        (payload: action.fetchUnauthorisedWordingsAction) =>
            dispatch(action.fetchUnauthorisedUserWordings(payload)),
        [dispatch],
    );

    useEffect(() => {
        if (
            claimApplicationId &&
            randomId &&
            !languageId &&
            currentCountryCode !== null &&
            !hasInitialised.current
        ) {
            hasInitialised.current = true;
            changeClaimReviewAnswerStateAction({
                claimApplicationId,
                randomId,
            });
            fetchClaimReviewValidityAction({
                claimApplicationId,
                randomId,
            });
            fetchUnauthorisedUserWordingsAction({
                payload: {
                    languageId:
                        currentCountryCode === codes.ukCode
                            ? codes.languageIdEnUk
                            : codes.languageIdEnCa,
                    claimApplicationId:
                        currentCountryCode === codes.canadaCode
                            ? claimApplicationId
                            : undefined,
                },
            });
        }
    }, [randomId, claimApplicationId, languageId, currentCountryCode]);

    useEffect(() => {
        if (wordings && languages) {
            syncTranslationsFromRedux(
                store.getState().applicationUser.wordings,
                languageId,
                store.getState().applicationSettings.apiApplicationSettings
                    .languages,
            );
            if (
                claimReview &&
                claimReview.claimApplicationId &&
                claimReview.randomId &&
                claimReview.email &&
                claimReview.passCode
            ) {
                fetchClaimReviewDetailsAction({
                    ClaimApplicationId: claimReview.claimApplicationId,
                    RandomId: claimReview.randomId,
                    Email: claimReview.email,
                    Passcode: claimReview.passCode,
                    LanguageId: languageId,
                });
            }
        }
    }, [wordings, languages, languageId, claimReview]);

    const handleUserInfoSubmit = (values: {
        email: string;
        passcode: string;
    }) => {
        changeClaimReviewAnswerStateAction({
            email: values.email,
            passCode: values.passcode,
        });

        fetchClaimReviewDetailsAction({
            ClaimApplicationId: claimReview.claimApplicationId,
            RandomId: claimReview.randomId,
            Email: values.email,
            Passcode: values.passcode,
            LanguageId: codes.emptyGuid,
        });
    };

    const acceptClaimReview = () => {
        acceptClaimReviewAction({
            ClaimApplicationId: claimReview.claimApplicationId,
            RandomId: claimReview.randomId,
            Email: claimReview.email,
            Passcode: claimReview.passCode,
            LanguageId: languageId,
        });
    };

    const getRejectionFormClaimReview = () => {
        fetchRejectionFormClaimReviewAction({
            ClaimApplicationId: claimReview.claimApplicationId,
            RandomId: claimReview.randomId,
            LanguageId: languageId,
        });
    };

    const rejectClaimReviewWithReason = (values: {
        acceptanceStatusReason: any;
    }) => {
        rejectClaimReviewWithReasonAction({
            ClaimApplicationId: claimReview.claimApplicationId,
            RandomId: claimReview.randomId,
            Email: claimReview.email,
            Passcode: claimReview.passCode,
            AcceptanceStatusReason: values.acceptanceStatusReason,
            LanguageId: languageId,
        });

        changeClaimReviewStateAction({ rejectedReasonClaimReview: null });
    };

    if (loading || isClaimReviewValid === null) {
        return <Loader />;
    }

    if (isClaimReviewValid === false) {
        return <PageNotFound />;
    }

    return (
        <PolicyHolderReviewClaimModal
            claimReview={claimReview}
            claimReviewAnswersGroupedByStep={claimReviewAnswersGroupedByStep}
            claimReviewOverview={claimReviewOverview}
            handleUserInfoSubmit={handleUserInfoSubmit}
            submitting={submitting}
            acceptClaimReview={acceptClaimReview}
            getRejectionFormClaimReview={getRejectionFormClaimReview}
            rejectClaimReviewWithReason={rejectClaimReviewWithReason}
            acceptedRejectedWordings={acceptedRejectedWordings}
            rejectedReasonClaimReviewWordings={
                rejectedReasonClaimReviewWordings
            }
            changeClaimReviewStateAction={changeClaimReviewStateAction}
            rejectFormLoading={rejectFormLoading}
        />
    );
};

export default PolicyHolderReviewClaimContainer;
